import { apiClient } from '../http';

export interface ReferralProgramTerms {
    appliesTo: string;
    commissionDurationMonths: number;
    commissionPercent: number;
    holdingPeriodDays: number;
    policyUrl: string;
}

export interface ReferralSummaryStats {
    clicks: number | null;
    conversions: number;
    currency?: string;
    earnings: number | null;
    leads: number;
    saleAmount: number;
    sales: number;
}

export interface ReferralTrendPoint {
    date: string;
    value: number;
}

export interface ReferralSummaryTrends {
    clicks: ReferralTrendPoint[];
    earnings: ReferralTrendPoint[];
    leads: ReferralTrendPoint[];
    sales: ReferralTrendPoint[];
}

export interface ReferralSummary {
    analytics: {
        endDate?: string;
        interval: ReferralAnalyticsInterval;
        startDate?: string;
    };
    program: {
        provider: string;
        terms: ReferralProgramTerms;
    };
    partner: {
        code: string;
        fallbackUrl: string;
        referralUrl: string;
        shortLink: string | null;
        stats: ReferralSummaryStats;
        trends: ReferralSummaryTrends;
    };
}

export type ReferralAnalyticsInterval = '7d' | '30d' | '90d' | '1y' | 'all';

export type ReferralActivityType = 'transactions' | 'payouts' | 'invitations';

export interface ReferralInvoiceableItem {
    interval: 'annual' | 'monthly' | null;
    key: string;
    label: string | null;
    quantity: number;
    type: 'credit' | 'featureAddOn' | 'plan' | 'unknown';
}

export interface ReferralTransaction {
    amount: number;
    createdAt: string;
    currency: string;
    description?: string | null;
    earnings: number | null;
    id: string;
    invoiceId?: string | null;
    invoiceableItems: ReferralInvoiceableItem[];
    status: string;
    type: 'click' | 'lead' | 'sale' | 'referral' | 'custom';
}

export interface ReferralPayout {
    amount: number;
    createdAt: string;
    currency: string;
    description?: string | null;
    id: string;
    initiatedAt: string | null;
    invoiceId: string | null;
    paidAt: string | null;
    periodEnd: string | null;
    periodStart: string | null;
    status: string;
}

export interface ReferralInvitation {
    createdAt: string;
    emailAddress: string;
    id: string;
    registeredAt: string | null;
    sentAt: string | null;
    status: 'Pending' | 'Sent' | 'Failed' | 'Registered';
}

export interface ReferralActivityItemMap {
    invitations: ReferralInvitation;
    payouts: ReferralPayout;
    transactions: ReferralTransaction;
}

export interface ReferralActivityPage<TType extends ReferralActivityType> {
    hasMore: boolean;
    items: ReferralActivityItemMap[TType][];
    nextCursor: string | null;
    pageSize: number;
    total: number;
    unavailable?: boolean;
}

export interface ReferralInvitationBatchResult {
    failedCount: number;
    invitations: ReferralInvitation[];
    sentCount: number;
}

export const referralService = {
    createInvitations: (emailAddresses: string[]) =>
        apiClient.post<ReferralInvitationBatchResult, { emailAddresses: string[] }>('/api/referrals/invitations', {
            emailAddresses,
        }),
    getActivity: <TType extends ReferralActivityType>(
        type: TType,
        cursor: string | null,
        pageSize: number,
        signal?: AbortSignal,
    ) =>
        apiClient.get<ReferralActivityPage<TType>, { cursor?: string; pageSize: number; type: TType }>(
            '/api/referrals/activity',
            { params: { cursor: cursor ?? undefined, pageSize, type }, signal },
        ),
    getSummary: (interval: ReferralAnalyticsInterval = '30d', signal?: AbortSignal) =>
        apiClient.get<ReferralSummary, { interval: ReferralAnalyticsInterval }>('/api/referrals', {
            params: { interval },
            signal,
        }),
};
